import { ElMessage } from 'element-plus';
import { defineStore } from 'pinia';
import invariant from 'tiny-invariant';

import { client, getErrorMessage } from '@/api';
import {
  CategoryCollectionDTO,
  CollectionGameDTO,
  CollectionGameModification,
  ModificationType,
  PaginatedCollectionGamesResponse,
} from '@/api/models';
import { maxValue } from '@/lib/max-value';
import { useCollectionGames } from './collection-games';

export type CategoryCollectionsState = {
  isWatching: boolean;
  loading: boolean;
  error: string | null;

  categoryCollection: CategoryCollectionDTO | null;

  collectionGames: CollectionGameDTO[];

  collectionGamesPagination: {
    page: number;
    pageSize: number;
    total: number;
    pageCount: number;
  };
};

interface LinkCollectionToCategoryParams {
  categorySlug: string;
  localeId: number;
  collectionId: string;
  prevCollectionId?: string;
}

interface UnlinkCollectionFromCategoryParams {
  categorySlug: string;
  localeId: number;
  collectionId: string;
}

interface FetchCategoryCollectionParams {
  categorySlug: string;
  localeId: number;
}

export const useCategoryCollectionsStore = defineStore('categoryCollections', {
  state: (): CategoryCollectionsState => ({
    isWatching: false,
    loading: false,
    error: null,
    categoryCollection: null,
    collectionGames: [],
    collectionGamesPagination: {
      page: 1,
      pageSize: 15,
      total: 0,
      pageCount: 0,
    },
  }),

  actions: {
    init() {
      if (this.isWatching) {
        return;
      }

      this.isWatching = true;
    },

    setLoading(value: boolean) {
      this.loading = value;
    },

    setError(value: string | null) {
      this.error = value;
    },

    setCategoryCollection(value: CategoryCollectionDTO | null) {
      this.categoryCollection = value;
    },

    async linkCollectionToCategory({
      categorySlug,
      localeId,
      collectionId,
      prevCollectionId,
    }: LinkCollectionToCategoryParams): Promise<void> {
      this.setError(null);
      this.setLoading(true);

      try {
        if (prevCollectionId) {
          await client.collectionsApi.deleteCategoryCollection(
            categorySlug,
            prevCollectionId,
            localeId,
          );
        }

        const categoryCollection =
          await client.collectionsApi.createCategoryCollection(categorySlug, {
            collectionId,
            localeId,
          });

        this.setCategoryCollection(categoryCollection);

        // games will be fetched automatically through watch in init()
        ElMessage.success('Collection linked successfully');
      } catch (err) {
        const message = getErrorMessage(err, 'Error linking collection');
        ElMessage.error(message);
        this.setError(message);
      } finally {
        this.setLoading(false);
      }
    },

    async unlinkCollectionFromCategory({
      categorySlug,
      localeId,
      collectionId,
    }: UnlinkCollectionFromCategoryParams): Promise<void> {
      this.setError(null);
      this.setLoading(true);

      try {
        await client.collectionsApi.deleteCategoryCollection(
          categorySlug,
          collectionId,
          localeId,
        );
        this.setCategoryCollection(null);

        ElMessage.success('Collection unlinked successfully');
      } catch (err) {
        const message = getErrorMessage(err, 'Error unlinking collection');
        ElMessage.error(message);
        this.setError(message);
      } finally {
        this.setLoading(false);
      }
    },

    async fetchCategoryCollection({
      categorySlug,
      localeId,
    }: FetchCategoryCollectionParams): Promise<void> {
      this.setError(null);
      this.setLoading(true);

      try {
        const categoryCollections =
          await client.collectionsApi.getCategoryCollections(categorySlug, {
            localeId,
          });
        invariant(
          categoryCollections.items.length <= 1,
          'Multiple or no category collections found',
        );

        if (categoryCollections.items.length === 1) {
          this.setCategoryCollection(categoryCollections.items[0]);
        }

        if (categoryCollections.items.length === 0) {
          this.setCategoryCollection(null);
        }
      } catch (err) {
        const message = getErrorMessage(err, 'Error getting linked collection');
        // Don't show error for 104 (no collection found)
        const errorCode = (err as any)?.code;
        if (errorCode !== 104) {
          ElMessage.error(message);
          this.setError(message);
        } else {
          this.setCategoryCollection(null);
        }
      } finally {
        this.setLoading(false);
      }
    },

    async addGamesToCollection(gameIds: string[]): Promise<void> {
      invariant(this.categoryCollection?.collectionId, 'Category collection not found');

      this.setError(null);
      this.setLoading(true);

      try {
        const collectionGamesStore = useCollectionGames();
        const maxPos = maxValue('position', collectionGamesStore.editingGames);

        const games: CollectionGameModification[] = gameIds.map((gameId, i) => ({
          gameSlug: gameId.toLowerCase(),
          modificationType: 'INSERT' as ModificationType,
          isManual: true,
          position: maxPos + i + 1,
        }));

        await client.collectionsApi.updateCollection(
          this.categoryCollection.collectionId,
          { games },
        );

        await collectionGamesStore.initEditingGames(this.categoryCollection.collectionId);
        await this.fetchCollectionGames();
      } catch (err) {
        const message = getErrorMessage(err, 'Error adding games to collection');
        ElMessage.error(message);
        throw err;
      } finally {
        this.setLoading(false);
      }
    },

    async fetchCollectionGames() {
      invariant(
        this.categoryCollection?.collectionId,
        'Category collection not found',
      );

      this.setError(null);
      this.setLoading(true);

      try {
        const data = await client.collectionsApi.listCollectionGames(
          this.categoryCollection.collectionId,
          {
            pageNumber: this.collectionGamesPagination.page,
            pageSize: this.collectionGamesPagination.pageSize,
          },
        );

        const { items = [], total, page, pageSize, pageCount } = data;
        this.collectionGames = items;
        this.collectionGamesPagination = {
          page,
          pageSize,
          total,
          pageCount,
        };

        return data;
      } catch (err) {
        const message = getErrorMessage(err, 'Error loading collection games');
        ElMessage.error(message);
        this.setError(message);
        return {
          items: [],
          page: 1,
          pageSize: 15,
          total: 0,
          pageCount: 0,
        } as PaginatedCollectionGamesResponse;
      } finally {
        this.setLoading(false);
      }
    },
  },
});
